import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";

import {
	Alert,
	Button,
	HookFormInputGroup,
	SpinnerLoader,
	Tooltip,
} from "components";
import { UserProfileFields, FeatureManager } from "globalConstants";
import { getResidualsRole } from "helpers";
import { useCheckIfEmailExist } from "hooks";
import { useResetPasswordFAMutation, useResetPasswordMutation } from "queries";
import { uiActions } from "store";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import { emailValid, required } from "validators/validation";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import styles from "../userForm.module.scss";

type EmailFieldProps = {
	isLoading?: boolean;
	defaultValues?: {
		email?: string;
		userFullName?: string;
		userId?: string;
		fullName?: string;
		fusionAuthUserId?: string;
		isManuallyLinkedWithFusionAuth?: boolean;
		agentId?: string | number;
		role?: {
			description: string;
			id: number;
			isUserSpecific: boolean;
			name: string;
		};
		company?: { id: string };
	};
	refetchUser?: Function;
	isEditPage?: boolean;
	isActiveUser: boolean;
	isHiddenSsoId?: boolean;
	companyId: string;
	userRole?: { [key: string]: any };
};

const EmailField = ({
	isLoading = false,
	defaultValues,
	refetchUser,
	isEditPage = false,
	isActiveUser,
	isHiddenSsoId = false,
	companyId,
	userRole,
}: EmailFieldProps) => {
	const { Email, AgentId, FusionAuthUserId, IsManuallyLinkedWithFusionAuth } =
		UserProfileFields;

	const dispatch = useDispatch();

	const {
		register,
		trigger,
		setError,
		clearErrors,
		watch,
		formState: { errors },
	} = useFormContext();

	const value = watch("email");
	const role = userRole?.name || watch("role.name");

	const {
		handleChange: handleEmailChange,
		handleBlur: handleEmailBlur,
		isLoading: isCheckEmailLoading,
		validator: emailAllowed,
		handleAgentOfficeChange,
		handleRoleChange,
		allowedDomains,
	} = useCheckIfEmailExist({
		fieldName: "email",
		fieldInitialValue: defaultValues?.[Email.name],
		trigger,
		setError,
		clearErrors,
		companyId,
		errorMessage: errors?.email?.message,
	});

	useEffect(() => {
		const checkEmail = async () => {
			let isInvalid;
			if (value && role) {
				isInvalid = await handleRoleChange(value, role);
			}
			if (!isInvalid) {
				companyId && handleAgentOfficeChange(value, companyId, role);
			}
		};
		if (!isLoading) {
			checkEmail();
		}
	}, [
		handleRoleChange,
		value,
		role,
		isLoading,
		companyId,
		handleAgentOfficeChange,
		errors,
	]);

	const handleBlur = (e: any) => {
		handleEmailBlur(e, role);
	};

	const useResetPassword = FeatureManager.isEnabled("fusionAuth")
		? useResetPasswordFAMutation
		: useResetPasswordMutation;

	const { mutate: resetPassword, isLoading: isResetPasswordLoading } =
		useResetPassword();

	const handleClickSsoIdButton = () => {
		dispatch(
			uiActions.showModal("SSO_ID_USERS", {
				userFullName: defaultValues?.fullName,
				userId: defaultValues?.[AgentId.name],
				fusionAuthUserId: defaultValues?.[FusionAuthUserId.name],
				refetchUser,
				email: defaultValues?.email,
			}),
		);
	};

	const handleClickResetPasswordButton = () =>
		resetPassword({ id: defaultValues?.agentId });

	const isResidualsRoleDefaultValue = getResidualsRole(
		defaultValues?.role?.name,
	);

	const WarningIconComponent = () =>
		!isHiddenSsoId &&
		!defaultValues?.[FusionAuthUserId.name] &&
		isEditPage &&
		isActiveUser &&
		!isResidualsRoleDefaultValue ? (
			<Tooltip
				html={
					<div className={styles.warningIconTooltip}>
						User doesn’t have SSO ID. To enable them of logging to
						RISE please set up a link by clicking SSO ID button
					</div>
				}
				disabled={false}
				position="bottom"
				size="small"
			>
				<div className={styles.warningIconWrapper}>
					<WarningIcon />
				</div>
			</Tooltip>
		) : null;

	const isManuallyLinked =
		(defaultValues?.[FusionAuthUserId.name] &&
			defaultValues?.[IsManuallyLinkedWithFusionAuth.name]) ||
		false;

	const ActionButtons = () =>
		isEditPage && isActiveUser && !isResidualsRoleDefaultValue ? (
			<div className="d-flex">
				{!isHiddenSsoId &&
					(!defaultValues?.[FusionAuthUserId.name] ||
						isManuallyLinked) && (
						<Button
							type="button"
							variant="secondary"
							value="SSO&nbsp;ID"
							isLoading={isLoading}
							minWidth="auto"
							onClick={handleClickSsoIdButton}
						/>
					)}
				{defaultValues?.[FusionAuthUserId.name] && (
					<Button
						type="button"
						variant="secondary"
						value="Reset&nbsp;User&nbsp;Password"
						isLoading={isLoading || isResetPasswordLoading}
						minWidth="auto"
						onClick={handleClickResetPasswordButton}
					/>
				)}
			</div>
		) : null;

	return (
		<>
			<Row>
				<Col lg={5}>
					<HookFormInputGroup
						name={Email.name}
						register={register}
						validator={{
							required,
							emailValid,
							emailAllowed,
						}}
						colLabel={5}
						colInput={7}
						label={Email.displayName}
						errors={errors}
						normalizeOnBlur={trimNormalizer}
						normalize={trimMidSentenceNormalizer}
						showRequiredMark
						loading={isCheckEmailLoading}
						disabled={isCheckEmailLoading}
						loader={SpinnerLoader}
						onBlur={handleBlur}
						onChange={handleEmailChange}
						blurLoading={isLoading}
						readOnly={isManuallyLinked}
						addon={<WarningIconComponent />}
						button={<ActionButtons />}
					/>
				</Col>
			</Row>
			{allowedDomains?.length > 0 && (
				<Row>
					<Col lg={{ span: 10, offset: 2 }}>
						<Alert
							variant="notice"
							message={allowedDomains.join(", ")}
						/>
					</Col>
				</Row>
			)}
		</>
	);
};

export default EmailField;
